import { useMemo, useState } from "react";

import { ArrowUpRight, CircleCheckBig, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

import { usePromoteCategory, type Category, type Workspace } from "../api";
import { BOARD_META, SWOT_BOARDS, type Board, type SwotBoard } from "../constants";

/**
 * The read-out of the whole SWOT exercise: all four aspects side by side,
 * lanes ranked by weight (their share of the aspect's posted notes), with the
 * AI lane summaries inline. Check the themes that should carry forward and
 * push them onto the Impact vs Effort grid in one go.
 */
export function SwotSummaryTab({
  workspace,
  onNavigateBoard,
  highlight = null,
}: {
  workspace: Workspace;
  onNavigateBoard: (board: Board) => void;
  /** `data-planning-hl` key of a search-result target to shake/halo. */
  highlight?: string | null;
}) {
  const promote = usePromoteCategory(workspace.session.id);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [promoting, setPromoting] = useState(false);

  const promotedCategoryIds = useMemo(
    () =>
      new Set(
        workspace.notes
          .filter((n) => n.board === "impact_effort" && n.source_category_id)
          .map((n) => n.source_category_id as string),
      ),
    [workspace.notes],
  );

  const promoteChecked = async () => {
    setPromoting(true);
    let ok = 0;
    for (const id of checked) {
      try {
        await promote.mutateAsync({ id });
        ok += 1;
      } catch (err) {
        toast.error(err instanceof Error ? err.message : String(err));
      }
    }
    setPromoting(false);
    setChecked(new Set());
    if (ok) toast.success(`${ok} initiative${ok === 1 ? "" : "s"} sent to the grid`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Themes ranked by weight — the share of each aspect&apos;s notes they carry.
          Check the ones that should become initiatives.
        </p>
        <Button size="sm" disabled={checked.size === 0 || promoting} onClick={promoteChecked}>
          {promoting ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <ArrowUpRight className="mr-1 h-3.5 w-3.5" />
          )}
          Create {checked.size || ""} initiative{checked.size === 1 ? "" : "s"}
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {SWOT_BOARDS.map((board) => (
          <AspectSummary
            key={board}
            board={board}
            workspace={workspace}
            promotedCategoryIds={promotedCategoryIds}
            checked={checked}
            onToggle={(id, on) =>
              setChecked((prev) => {
                const next = new Set(prev);
                if (on) next.add(id);
                else next.delete(id);
                return next;
              })
            }
            onNavigateBoard={onNavigateBoard}
            highlight={highlight}
          />
        ))}
      </div>
    </div>
  );
}

function AspectSummary({
  board,
  workspace,
  promotedCategoryIds,
  checked,
  onToggle,
  onNavigateBoard,
  highlight = null,
}: {
  board: SwotBoard;
  workspace: Workspace;
  promotedCategoryIds: Set<string>;
  checked: Set<string>;
  onToggle: (categoryId: string, on: boolean) => void;
  onNavigateBoard: (board: Board) => void;
  highlight?: string | null;
}) {
  const meta = BOARD_META[board];
  const Icon = meta.icon;

  const posted = workspace.notes.filter((n) => n.board === board && n.status === "posted");
  const lanes = workspace.categories
    .filter((c) => c.board === board)
    .map((lane) => ({
      lane,
      count: posted.filter((n) => n.category_id === lane.id).length,
    }))
    .sort((a, b) => b.count - a.count);
  const uncategorized = posted.filter(
    (n) => !n.category_id || !lanes.some((l) => l.lane.id === n.category_id),
  ).length;

  return (
    <section className="space-y-2 rounded-md border bg-card p-4">
      <button
        type="button"
        className="flex items-center gap-2 text-sm font-semibold hover:text-brand"
        onClick={() => onNavigateBoard(board)}
      >
        <Icon className="h-4 w-4" />
        {meta.label}
        <span className="font-normal text-muted-foreground">
          {posted.length} note{posted.length === 1 ? "" : "s"}
        </span>
      </button>

      {lanes.length === 0 ? (
        <p className="text-xs text-muted-foreground">No themes yet — group the notes into lanes first.</p>
      ) : (
        <div className="space-y-2">
          {lanes.map(({ lane, count }) => (
            <LaneRow
              key={lane.id}
              lane={lane}
              count={count}
              total={posted.length}
              promoted={promotedCategoryIds.has(lane.id)}
              checked={checked.has(lane.id)}
              onToggle={(on) => onToggle(lane.id, on)}
              highlighted={highlight === `lanesum-${lane.id}`}
            />
          ))}
        </div>
      )}
      {uncategorized > 0 ? (
        <p className="text-[11px] text-muted-foreground">
          {uncategorized} note{uncategorized === 1 ? "" : "s"} still uncategorized.
        </p>
      ) : null}
    </section>
  );
}

function LaneRow({
  lane,
  count,
  total,
  promoted,
  checked,
  onToggle,
  highlighted = false,
}: {
  lane: Category;
  count: number;
  total: number;
  promoted: boolean;
  checked: boolean;
  onToggle: (on: boolean) => void;
  highlighted?: boolean;
}) {
  const weight = total ? Math.round((count / total) * 100) : 0;
  const canPromote = !promoted && count > 0;

  return (
    <div
      data-planning-hl={`lanesum-${lane.id}`}
      className={cn(
        "rounded-md border p-2.5",
        checked && "border-brand bg-brand/5",
        highlighted && "planning-highlight",
      )}
    >
      <div className="flex items-center gap-2">
        {promoted ? (
          <CircleCheckBig className="h-4 w-4 shrink-0 text-success" />
        ) : (
          <Checkbox
            checked={checked}
            disabled={!canPromote}
            onCheckedChange={(v) => onToggle(v === true)}
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-sm font-medium">{lane.name}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {count} · {weight}%
            </span>
          </div>
          <Progress value={weight} className="mt-1 h-1.5" />
        </div>
      </div>
      {lane.summary ? (
        <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-xs text-muted-foreground">
          {lane.summary}
        </p>
      ) : count > 0 ? (
        <p className="mt-2 text-[11px] italic text-muted-foreground">
          No AI summary yet — write one from the lane menu on the board.
        </p>
      ) : null}
      {promoted ? (
        <p className="mt-1 text-[11px] font-medium text-success">On the Impact vs Effort grid</p>
      ) : null}
    </div>
  );
}
